import { useMemo, useState } from "react";
import { configEnumLabel, configFieldLabel, configFieldRange, meshtasticSectionLabel } from '../lib/definitions/settings';
import { MESHTASTIC_ENUMS } from "../lib/meshtasticSchema.generated";
import type { MeshtasticField } from "../lib/meshtasticSchema.generated";

// Replaces the raw-JSON "Values" textarea for set_config / set_module_config
// / set_channel with real typed controls, one per protobuf field of the
// chosen section — task brief: "Replace every JSON textarea with real form
// controls... No JSON entry anywhere in the UI." Field existence, wire type,
// and enum membership come from meshtasticSchema.generated.ts (generated
// straight off the real Meshtastic protobufs the backend validates
// against — see that file's own header); human labels and semantic ranges
// come from the definitions.ts registry (configFieldLabel/configEnumLabel/
// configFieldRange) alongside every other config label in this app.
//
// SAFETY DESIGN — why partial, per-field "touched" tracking is not just
// simpler but the actually-safer choice, not a shortcut:
// The backend's write path (meshtastic_tcp.py's _ctl_write_config_section /
// _ctl_set_channel) takes a COPY of the device's real, currently-live
// protobuf message and MERGES the submitted `values` dict onto that copy
// (`tmp.CopyFrom(target); json_format.ParseDict(values, tmp, ...)`) — it is
// never built from a blank message. That means:
//   1. A field genuinely omitted from `values` is left at its true current
//      value, whatever this form displayed for it. A stale or missing
//      snapshot on our side can make what THIS FORM SHOWS wrong, but can
//      never make what gets WRITTEN wrong, provided we only ever submit
//      fields the operator actually touched.
//   2. So each field tracks its own touched/untouched state, exactly like
//      the outer param-level FieldState in RadioControlOperationCard
//      ("an untouched field means leave unchanged, never sent as its
//      displayed value") — just one level deeper. Only touched fields ever
//      appear in the object this component reports upward.
//   3. The existing dry-run/diff step is the real safety net regardless:
//      it diffs the backend's own live value against the result of the
//      merge, so the operator sees the truth before confirming, even if
//      this form's pre-fill were ever stale.
//
// Protobuf's MessageToDict (which produces the live snapshot this form
// pre-fills from) OMITS any field currently at its type's zero value
// (false / 0 / "" / the enum's value-0 member) — that omission means
// "currently at default," not "unknown." zeroValueFor() below supplies the
// same zero value purely for DISPLAY when a key is absent, so a field
// really at its default renders as e.g. 0 or an unchecked box instead of a
// misleading blank input. It never affects what gets submitted — only a
// touched field's own edited value does.

export type MeshtasticSchemaKind = "config" | "module" | "channel";

interface FieldDraft {
  touched: boolean;
  value: unknown;
}

function zeroValueFor(field: MeshtasticField): unknown {
  switch (field.kind) {
    case "bool":
      return false;
    case "int":
    case "uint":
    case "float":
      return 0;
    case "string":
    case "bytes":
      return "";
    case "enum": {
      const values = field.enumType ? MESHTASTIC_ENUMS[field.enumType] : undefined;
      const zero = values?.find((v) => v.value === 0);
      return zero?.name ?? values?.[0]?.name ?? "";
    }
    default:
      return undefined;
  }
}

// Channel writes flatten ChannelSettings' fields together with Channel's
// own (see meshtasticSchema.generated.ts's header: "ChannelSettings ...
// followed by Channel"). Of Channel's own fields, "index" is supplied by
// set_channel's separate required "Index" param (never part of `values`,
// so never rendered here), leaving "role" as the only Channel-level field
// mixed into the flattened list — every other name in
// MESHTASTIC_CHANNEL_FIELDS lives under the channel's nested `.settings`
// in the live snapshot (RadioConfigEntry.channels[i].settings.*).
const CHANNEL_TOP_LEVEL_FIELDS = new Set(["role"]);

function readCurrentValue(
  field: MeshtasticField,
  schemaKind: MeshtasticSchemaKind,
  currentSection: Record<string, unknown> | undefined,
): unknown {
  if (!currentSection) return undefined;
  if (schemaKind === "channel" && !CHANNEL_TOP_LEVEL_FIELDS.has(field.name)) {
    const settings = currentSection.settings;
    return settings && typeof settings === "object" ? (settings as Record<string, unknown>)[field.jsonName] : undefined;
  }
  return currentSection[field.jsonName];
}

const REDACTED_PLACEHOLDER = "<redacted>";

// Fields the backend redacts by name wherever they appear (meshtastic_tcp.py
// _REDACT_CONFIG_KEYS) — the live value is real but never sent to the
// browser, so pre-filling with the literal string "<redacted>" would be
// both wrong (that's not the device's actual value) and dangerous (typing
// nothing and submitting it back would round-trip a fake secret, which
// cleanRedacted() in RadioControlOperationCard also guards against as a
// backstop). These render as a blank input with an explicit "unchanged
// unless you type a new value" placeholder instead.
const SECRET_JSON_NAMES = new Set(["privateKey", "adminKey", "password", "psk"]);

function isRepeatablePrimitive(field: MeshtasticField): boolean {
  return field.repeated && (field.kind === "string" || field.kind === "bytes" || field.kind === "int" || field.kind === "uint" || field.kind === "float");
}

function parseRepeatedInput(text: string, kind: MeshtasticField["kind"]): unknown[] {
  const parts = text
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  if (kind === "int" || kind === "uint" || kind === "float") {
    return parts.map((p) => Number(p)).filter((n) => !Number.isNaN(n));
  }
  return parts;
}

function formatRepeatedValue(value: unknown): string {
  if (Array.isArray(value)) return value.join(", ");
  return "";
}

function FieldControl({
  field,
  schemaKind,
  draft,
  currentValue,
  onChange,
}: {
  field: MeshtasticField;
  schemaKind: MeshtasticSchemaKind;
  draft: FieldDraft;
  currentValue: unknown;
  onChange: (next: FieldDraft) => void;
}) {
  const label = configFieldLabel(field.name);
  const isSecret = SECRET_JSON_NAMES.has(field.jsonName);
  const isRedacted = isSecret && currentValue === REDACTED_PLACEHOLDER;
  const displayValue = draft.touched ? draft.value : isRedacted ? "" : (currentValue ?? zeroValueFor(field));

  if (field.kind === "message") {
    // One level of nested-message fields (e.g. network.ipv4Config) exist on
    // a handful of sections. Rather than silently drop them or fall back to
    // JSON entry for just this one field, they're named and explicitly
    // marked out of scope for this form rather than hidden.
    return (
      <div className="settings-field settings-field-span-full mesh-field-unsupported">
        <span>{label}</span>
        <span className="settings-field-desc">Nested structure — not editable in this form. Current value is left untouched.</span>
      </div>
    );
  }

  if (field.kind === "bytes" && field.repeated) {
    // e.g. security.admin_key (repeated bytes, base64) — vanishingly rare
    // and always a list of opaque keys; safest as a plain typed list rather
    // than any structured control.
  }

  if (isRepeatablePrimitive(field)) {
    return (
      <label className="settings-field settings-field-span-full">
        <span>
          {label} <span className="settings-field-desc">(comma-separated list)</span>
        </span>
        <input
          type="text"
          value={typeof displayValue === "string" ? displayValue : formatRepeatedValue(displayValue)}
          placeholder={isRedacted ? "Unchanged unless you enter new values" : undefined}
          onChange={(e) => onChange({ touched: true, value: parseRepeatedInput(e.target.value, field.kind) })}
        />
      </label>
    );
  }

  if (field.kind === "bool") {
    return (
      <label className="settings-field settings-field-span-full" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        <input type="checkbox" checked={displayValue === true} onChange={(e) => onChange({ touched: true, value: e.target.checked })} />
        <span>{label}</span>
      </label>
    );
  }

  if (field.kind === "enum") {
    const options = field.enumType ? MESHTASTIC_ENUMS[field.enumType] ?? [] : [];
    return (
      <label className="settings-field">
        <span>{label}</span>
        <select value={typeof displayValue === "string" ? displayValue : ""} onChange={(e) => onChange({ touched: true, value: e.target.value })}>
          {options.map((opt) => (
            <option key={opt.name} value={opt.name}>
              {configEnumLabel(opt.name)}
            </option>
          ))}
        </select>
      </label>
    );
  }

  if (field.kind === "int" || field.kind === "uint" || field.kind === "float") {
    const range = configFieldRange(field.name);
    return (
      <label className="settings-field">
        <span>
          {label}
          {range?.unit ? ` (${range.unit})` : ""}
        </span>
        <input
          type="number"
          min={range?.min}
          max={range?.max}
          step={range?.step ?? (field.kind === "float" ? "any" : 1)}
          value={typeof displayValue === "number" ? displayValue : displayValue === "" ? "" : Number(displayValue ?? 0)}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") {
              onChange({ touched: true, value: 0 });
              return;
            }
            const n = Number(raw);
            onChange({ touched: true, value: Number.isNaN(n) ? 0 : field.kind === "float" ? n : Math.trunc(n) });
          }}
        />
      </label>
    );
  }

  // string / bytes (non-repeated)
  return (
    <label className="settings-field">
      <span>{label}</span>
      <input
        type={isSecret ? "password" : "text"}
        value={typeof displayValue === "string" ? displayValue : ""}
        placeholder={isRedacted ? "Unchanged unless you enter a new value" : undefined}
        onChange={(e) => onChange({ touched: true, value: e.target.value })}
      />
    </label>
  );
}

export function MeshtasticValuesForm({
  schemaKind,
  sectionName,
  fields,
  currentSection,
  hasLiveSnapshot,
  onChange,
}: {
  schemaKind: MeshtasticSchemaKind;
  // Which named section this is (e.g. "lora", "mqtt") — undefined for
  // channel writes, which have one fixed field list, not a chosen section.
  sectionName?: string;
  fields: readonly MeshtasticField[];
  currentSection: Record<string, unknown> | undefined;
  // False when no live snapshot exists yet for this transport/section (e.g.
  // just connected, or this transport doesn't report device.config at all)
  // — the form still renders with type-zero-value placeholders (see the
  // safety-design comment at the top of this file for why that's still
  // safe to submit), but says so plainly rather than implying the zeros are
  // real device state.
  hasLiveSnapshot: boolean;
  onChange: (values: Record<string, unknown>) => void;
}) {
  const [drafts, setDrafts] = useState<Record<string, FieldDraft>>({});

  const renderableFields = useMemo(() => fields.filter((f) => f.kind !== "message"), [fields]);

  function updateField(field: MeshtasticField, next: FieldDraft) {
    const nextDrafts = { ...drafts, [field.jsonName]: next };
    setDrafts(nextDrafts);
    const out: Record<string, unknown> = {};
    for (const f of fields) {
      const d = nextDrafts[f.jsonName];
      if (d?.touched) out[f.jsonName] = d.value;
    }
    onChange(out);
  }

  return (
    <div className="mesh-values-form">
      {sectionName && <h5 className="mesh-values-form-title">{meshtasticSectionLabel(sectionName)}</h5>}
      {!hasLiveSnapshot && (
        <p className="settings-field-desc mesh-values-form-no-snapshot">
          No live snapshot for this section yet — fields below show protobuf defaults, not confirmed device state. Only
          fields you actually change here will be sent; nothing else is touched.
        </p>
      )}
      <div className="settings-fields-grid">
        {renderableFields.length === 0 ? (
          <p className="settings-field-desc">This section has no editable fields.</p>
        ) : (
          fields.map((field) => (
            <FieldControl
              key={field.jsonName}
              field={field}
              schemaKind={schemaKind}
              draft={drafts[field.jsonName] ?? { touched: false, value: undefined }}
              currentValue={readCurrentValue(field, schemaKind, currentSection)}
              onChange={(next) => updateField(field, next)}
            />
          ))
        )}
      </div>
    </div>
  );
}
